/* import __COLOCATED_TEMPLATE__ from './settings-v2.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type IntlService from 'ember-intl/services/intl';
import type Store from '@ember-data/store';
import type Fin from 'embercom/models/operator/visual-builder/step/fin';
import {
  type AllChannels as Channel,
  EXTERNAL_TICKET_CHANNELS,
} from 'embercom/lib/operator/custom-bots/constants';
import { FIN_CSAT_COMPATIBLE_CHANNELS } from 'embercom/lib/operator/resolution-bot/constants';
import type EditorState from 'embercom/objects/workflows/graph-editor/editor-state';
import type FinConfiguration from 'embercom/objects/visual-builder/configuration/step/fin';
import { State } from 'embercom/models/data/matching-system/matching-constants';

interface Signature {
  Args: {
    step: Fin;
    isReadOnly: boolean;
    customAnswerCount: number;
    triageButtonLabel: string;
    thatHelpedButtonLabel: string;
    canUseAiAnswers: boolean;
    usesResolutionsWithCustomAnswers: boolean;
    canUseCustomAnswers: boolean;
    editorState: EditorState;
    stepConfig: FinConfiguration;
    hasShopifyConnected: boolean;
    isMessengerInstalledForWeb: boolean;
  };
}

export default class SettingsV2 extends Component<Signature> {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare contentEditorService: any;
  @service declare appService: AppService;
  @service declare attributeService: any;

  @tracked duringAccordionOpenSectionId = '';
  @tracked chatAccordionOpenSectionId = '';
  @tracked emailAccordionOpenSectionId = '';
  @tracked endAccordionOpenSectionId = '';
  @tracked isUsageDrawerOpen = false;
  @tracked showCsatBlockAfterUpdate =
    this.args.step.csatBlockUpdateAfter !== undefined &&
    this.args.step.csatBlockUpdateAfter !== null;

  // --- Data getters (used by Follow Training) ---

  get liveGuidelines() {
    return this.store
      .peekAll('ai-agent/guidelines')
      .rejectBy('isDeleted')
      .filterBy('state', State.Live)
      .toArray();
  }

  get liveGuidelinesCount() {
    return this.liveGuidelines.length;
  }

  get categoriesCount() {
    return this.attributeService.aiCategoriesDescriptors.filterBy('isEnabled', true).length;
  }

  get escalationGuidanceCount() {
    return this.liveGuidelines.filter((guideline) => guideline.category === 'routing').length;
  }

  get escalationRulesCount() {
    return this.store.peekAll('ai-agent/escalation-rule').filterBy('enabled', true).length;
  }

  // --- Visibility getters ---

  get appId() {
    return this.appService.app.id;
  }

  get shouldShowSetExpectationsSettings() {
    return !this.args.stepConfig ? true : this.args.stepConfig?.showFinSetExpectationsSection;
  }

  get shouldShowPreHandoverSettings() {
    return !this.args.stepConfig ? true : this.args.stepConfig?.showFinPreHandoverSection;
  }

  get shouldShowConfirmResolutionSettings() {
    return !this.args.stepConfig ? false : this.args.stepConfig?.showConfirmResolutionSettings;
  }

  get shouldShowHandoffToExternalTicketOnResolutionSection() {
    let isZendesk = this.appService.app.hasStandalonePlatform('zendesk');
    let isSalesforce = this.appService.app.hasStandalonePlatform('salesforce');
    let isZendeskWithFlag =
      isZendesk &&
      this.appService.app.canUseFeature('team-standalone-zendesk-ticket-handoff-on-resolution');
    if (this.args.stepConfig && (isSalesforce || isZendeskWithFlag)) {
      return this.args.stepConfig?.showHandoffToExternalTicketOnResolutionSection;
    }
    return false;
  }

  get shouldShowFollowUpSettings() {
    if (
      !this.appService.app?.canUseFeature('answerbot-conversational-fin') ||
      (!this.appService.app?.canUseFeature('answerbot-conversational-opt-in') &&
        this.appService.app?.canUseFeature('answerbot-conversational-fin-opt-out'))
    ) {
      return false;
    }
    if (!this.args.stepConfig) {
      return true;
    }
    return (
      this.args.stepConfig?.showFollowUpSettings &&
      this.args.stepConfig?.showFinSetExpectationsSection
    );
  }

  get shouldShowEmailFollowUpSettings() {
    let workflow = this.args.editorState?.workflow;
    if (workflow?.workflowInstanceTrigger === 'no-trigger') {
      return true;
    }
    return this.targetingEmail;
  }

  get shouldShowCsatSettings() {
    return !this.args.stepConfig ? true : this.args.stepConfig?.showFinCsatSection;
  }

  get shouldShowAutoCloseSettings() {
    return !this.args.stepConfig ? true : this.args.stepConfig?.showFinAutoCloseSection;
  }

  get shouldShowHardResolutionWorkflowSettings() {
    return !this.args.stepConfig ? false : this.args.stepConfig?.showHardResolutionWorkflowSettings;
  }

  get shouldShowUsageManagement() {
    return !this.args.stepConfig ? false : this.args.stepConfig?.showUsageManagement;
  }

  get shouldShowMessengerSettings() {
    return !this.args.stepConfig ? false : this.args.stepConfig?.showMessengerSettings;
  }

  // --- Computed state getters ---

  get oneTimeModeAvailable() {
    return (
      this.args.step.useCustomAnswers &&
      !this.args.step.useAiAnswers &&
      this.args.step.botMode !== 'bot_only'
    );
  }

  get botOnlyModeAvailable() {
    return this.args.step.botMode !== 'one_time';
  }

  get channelIncompatibleWithFinCSAT() {
    let channels = this.contentEditorService.activeObject?.targetChannels || [];
    if (!channels.length) {
      return false;
    }
    return channels.filter((channel: Channel) => {
      return !FIN_CSAT_COMPATIBLE_CHANNELS.includes(channel);
    }).length;
  }

  get emailLikeChannel() {
    let channels = this.contentEditorService.activeObject?.targetChannels || [];
    return channels.find(
      (channel: Channel) => channel === 'email' || EXTERNAL_TICKET_CHANNELS.includes(channel),
    );
  }

  get targetingEmail() {
    let channels = this.contentEditorService.activeObject?.targetChannels || [];
    if (!channels.length) {
      return false;
    }
    return channels.some(
      (channel: Channel) => channel === 'email' || EXTERNAL_TICKET_CHANNELS.includes(channel),
    );
  }

  get targetingChat() {
    let channels: Channel[] = this.contentEditorService.activeObject?.targetChannels || [];
    if (!channels.length) {
      return false;
    }
    return channels.some(
      (channel: Channel) =>
        channel !== 'email' &&
        channel !== 'phone_call' &&
        !EXTERNAL_TICKET_CHANNELS.includes(channel),
    );
  }

  get isMultiChannelMode() {
    let workflow = this.args.editorState?.workflow;
    if (workflow?.workflowInstanceTrigger === 'no-trigger') {
      return true;
    }
    return Boolean(this.targetingEmail) && Boolean(this.targetingChat);
  }

  get answerTypeSummary() {
    if (this.args.step.useCustomAnswers) {
      if (this.args.step.useAiAnswers) {
        return this.intl.t(
          'operator.workflows.visual-builder.pass-to-fin.settings.control-answer-type.ai-and-custom-answers',
        );
      } else {
        return this.intl.t(
          'operator.workflows.visual-builder.pass-to-fin.settings.control-answer-type.custom-answers',
        );
      }
    }
    return this.intl.t(
      'operator.workflows.visual-builder.pass-to-fin.settings.control-answer-type.ai-answers',
    );
  }

  get previewConfiguration() {
    return this.store.peekRecord('messenger-settings/all', this.appService.app.id)
      ?.previewConfiguration;
  }

  // --- Actions ---

  @action toggleAiAnswers() {
    this.args.step.useAiAnswers = !this.args.step.useAiAnswers;
    if (this.args.step.useAiAnswers && this.args.step.botMode !== 'bot_only') {
      this.args.step.botMode = 'looping';
    } else if (!this.args.step.useAiAnswers) {
      this.args.step.preHandoverAnswerEnabled = false;
      if (!this.args.step.useCustomAnswers) {
        this.args.step.useCustomAnswers = true;
      }
    }
  }

  @action toggleCustomAnswers() {
    this.args.step.useCustomAnswers = !this.args.step.useCustomAnswers;
    if (this.args.step.useCustomAnswers && this.args.step.botMode !== 'bot_only') {
      this.args.step.botMode = 'looping';
    } else if (!this.args.step.useAiAnswers && !this.args.step.useCustomAnswers) {
      this.args.step.useAiAnswers = true;
      this.args.step.botMode = 'looping';
    }
  }

  @action toggleOneTimeMode() {
    this.args.step.botMode = this.args.step.botMode === 'one_time' ? 'looping' : 'one_time';
    if (this.args.step.botMode === 'one_time') {
      this.args.step.preHandoverAnswerEnabled = false;
    }
  }

  @action toggleBotOnlyMode() {
    this.args.step.botMode = this.args.step.botMode === 'bot_only' ? 'looping' : 'bot_only';
    if (this.args.step.botMode === 'bot_only') {
      this.args.step.preHandoverAnswerEnabled = false;
    }
  }

  @action toggleCsatEnabledForHardResolution() {
    this.args.step.csatEnabledForHardResolution = !this.args.step.csatEnabledForHardResolution;
    this.settleCsatBlockAfterUpdate();
  }

  @action toggleCsatEnabledForSoftResolution() {
    this.args.step.csatEnabledForSoftResolution = !this.args.step.csatEnabledForSoftResolution;
    this.settleCsatBlockAfterUpdate();
  }

  @action updateCsatCustomMessage(content: string) {
    this.args.step.csatCustomMessage = content;
    this.settleCsatBlockAfterUpdate();
  }

  @action toggleUseCsatCustomMessage() {
    this.args.step.useCsatCustomMessage = !this.args.step.useCsatCustomMessage;
    this.settleCsatBlockAfterUpdate();
  }

  @action toggleConfirmResolution() {
    this.args.step.confirmResolutionEnabled = !this.args.step.confirmResolutionEnabled;
  }

  settleCsatBlockAfterUpdate() {
    if (this.args.step.isAnyCSATSettingEnabled) {
      return;
    }
    this.args.step.csatBlockUpdateAfter = undefined;
    this.showCsatBlockAfterUpdate = false;
  }

  @action toggleRatingChange() {
    this.showCsatBlockAfterUpdate = !this.showCsatBlockAfterUpdate;
    if (!this.showCsatBlockAfterUpdate) {
      this.args.step.csatBlockUpdateAfter = undefined;
    } else if (
      this.args.step.csatBlockUpdateAfter === undefined ||
      this.args.step.csatBlockUpdateAfter === null
    ) {
      this.args.step.csatBlockUpdateAfter = 0;
    }
  }

  @action setBlockUpdateAfter(e: InputEvent) {
    let value = parseInt((e.target as HTMLInputElement).value, 10);
    if (isNaN(value)) {
      value = 0;
    }
    this.args.step.csatBlockUpdateAfter = value * 3600;
  }

  @action openUsageDrawer() {
    this.isUsageDrawerOpen = true;
  }

  @action onUsageDrawerOpenChange(isOpen: boolean) {
    this.isUsageDrawerOpen = isOpen;
  }

  @action openAndScrollToAccordionSection(sectionId: string) {
    this.endAccordionOpenSectionId = sectionId;
    requestAnimationFrame(() => {
      let targetElement = document.querySelector(`[data-accordion-section="${sectionId}"]`);
      targetElement?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest',
      });
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Workflows::GraphEditor::NodeItems::Steps::Fin::SettingsV2': typeof SettingsV2;
    'workflows/graph-editor/node-items/steps/fin/settings-v2': typeof SettingsV2;
  }
}
